import { firestore } from "firebase";

/**
 * Materialised index for the RENO prediction-review queue.
 *
 * Written **only** by IRES Assist through the Admin SDK (`services/reno_review_index.py`); Office
 * reads it and never writes. The authoritative field reference lives in the Assist repo at
 * `docs/firestore-data-model.md` §9.
 *
 * Two collections rather than one because the queue is traversed device-major then chain-newest-first
 * while the age filter is a range on `chainStart`, and Firestore requires the first `orderBy` to be
 * the range-filtered field. The pinned `@firebase/firestore@1.18.0` enforces that locally, throwing
 * `INVALID_ARGUMENT` before a request is even sent, so the split is a hard requirement rather than an
 * optimisation.
 */

/**
 * The RENO model version the review queue is scoped to.
 *
 * Ratings are per model version (D2): a new RENO release re-queues every drying for a fresh pass, so
 * this must be bumped when Assist ships one. **The source of truth is Assist's
 * `models/manifest.json`**, which is where the backfill and the incremental writer both read it
 * from; this constant only has to agree with it.
 *
 * Hardcoded rather than derived from the data on purpose: picking "the newest version present in the
 * index" would silently switch the whole queue the moment Assist wrote a single entry with a new
 * version, part-way through a reviewer's pass. Reading it from Assist's API would be better still
 * and is worth doing if the version starts changing often.
 */
export const CURRENT_RENO_MODEL_VERSION = "R2.0";

/** Firestore collection holding one document per device that has reviewable chains. */
export const RENO_REVIEW_DEVICES_COLLECTION = "renoReviewDevices";

/** Firestore collection holding one document per reviewable chain. */
export const RENO_REVIEW_QUEUE_COLLECTION = "renoReviewQueue";

/**
 * Device type, derived by Assist from the serial prefix rather than the device document's
 * `comBoxType` field, which is absent on about a third of production comboxes.
 *
 * Only `BOX` and `MINI` occur today because RENO is ComBox-only, but the field is generic because
 * Panels are expected to get predictions later.
 */
export enum RenoReviewDeviceType {
  BOX = "BOX",
  MINI = "MINI",
  SLIM = "SLIM",
  EDGE = "EDGE",
  PANEL = "PANEL",
  UNKNOWN = "UNKNOWN",
}

/** One device on the queue's device track, ordered by `serialNo`. */
export interface RenoReviewDevice {
  serialNo: string;
  deviceType: RenoReviewDeviceType;
  /** Current owner, informational only. */
  clientId: string | null;
  modelVersion: string;
  /** Aggregates, maintained by Assist. Absent on documents written by a partial backfill. */
  newestChainStart?: firestore.Timestamp | null;
  qualifyingChainCount?: number | null;
  updatedAt?: firestore.Timestamp | null;
}

/**
 * One reviewable drying (program chain).
 *
 * Document id is `{serialNo}__{chainId}`, which makes every Assist write an idempotent upsert.
 */
export interface RenoReviewQueueEntry {
  serialNo: string;
  chainId: string;
  /** The chain's `assignedClientId`, i.e. whose drying this was. */
  clientId: string | null;
  deviceType: RenoReviewDeviceType;
  /** Primary sort key, and the field the age filter ranges over. */
  chainStart: firestore.Timestamp;
  /** Null while the drying is still running, and for chains with no usable run end date. */
  chainEnd: firestore.Timestamp | null;
  /**
   * Whether a drying is going on *right now*.
   *
   * Assist derives this from the last run's `activeRun` flag (plus a sentinel end date), matching
   * Office's own `isActiveRun()`. Deliberately **not** Assist's `is_chain_active()`, which reports
   * chains that were merely never closed: on production that flags ~11% of chains, ~69% of which
   * last ran over a year ago. Roughly 1.9% of the queue is active by this definition.
   */
  isActive: boolean;
  modelVersion: string;
  modelSet: string | null;
  /**
   * Always `"edt"` for indexed entries.
   *
   * Assist only indexes the EDT program group: room drying resolves to the same `modelVersion`
   * string (`R2.0`), so filtering on the version alone would put a different model family in front
   * of reviewers.
   *
   * The gate is `INDEXED_PROGRAM_GROUPS` in Assist's `services/reno_review_index.py`, applied when
   * the entry is written — there is deliberately no program filter on the Office side to find. Note
   * that this does **not** track the device-detail view, which serves programs 3 (monitoring) and 8
   * (raumtrocknung) too; see `docs/admin/reno-review-queue.md`.
   */
  programGroup: string | null;
  trafficLight: string | null;
  /** Null on chains predicted before Assist began publishing the score on the chain summary. */
  qualityScore: number | null;
  programNumber: number | null;
  chainNumber: number | null;
  updatedAt?: firestore.Timestamp | null;
}

/**
 * Filters the reviewer picks on the queue screen.
 *
 * `maxAgeMonths` and `activeOnly` are **alternatives, never combined**: each needs its own composite
 * index, and the deployed pair covers one or the other. The UI must enforce that.
 */
export interface RenoReviewFilters {
  modelVersion: string;
  /** Empty means no device-type restriction. Firestore caps `in` at 10 values. */
  deviceTypes: RenoReviewDeviceType[];
  maxAgeMonths: number | null;
  /**
   * Earliest `chainStart` to include, as an absolute date.
   *
   * The same constraint as {@link maxAgeMonths} expressed differently, so the two share the one
   * `chainStart >=` slot and are offered as alternatives. See `chainStartCutoff`, which resolves
   * whichever is set into the single bound every query and count uses.
   */
  fromDate: Date | null;
  activeOnly: boolean;

  /**
   * Keep only dryings owned by this client, i.e. the chain's `assignedClientId`.
   *
   * Whose drying it *was*, not who owns the device now, which is why it comes off the queue entry
   * rather than the device track.
   */
  clientId: string | null;

  /**
   * Keep only dryings this reviewer reported as having a broken program chain.
   *
   * Reads the `notes` store, so it is an anti-join like the rating filters: Firestore cannot join the
   * queue against a reviewer's own documents.
   */
  onlyChainSuspect: boolean;

  /** Keep only dryings this reviewer wrote a comment on. Also from the `notes` store. */
  onlyWithComment: boolean;

  /**
   * Keep only dryings this reviewer has hidden, and stop subtracting them.
   *
   * Suppressions are subtracted from every other pass unconditionally — that is what "nicht mehr
   * zeigen" means — so this filter is the **one** way back to them in the stepper. It exists because
   * hiding is now a toggle: a mark that cannot be found again cannot be un-marked either, and the
   * un-hide screen only deletes a suppression, it does not open the drying.
   *
   * Reads the `suppressions` store, so it is an anti-join like {@link onlyChainSuspect}, and it
   * combines freely with a rating: a drying can be hidden and scored at the same time.
   */
  onlySuppressed: boolean;

  /**
   * Skip dryings this reviewer has already scored for this model version.
   *
   * Applied client-side after the queue query, because Firestore cannot anti-join. Note that
   * suppressions are subtracted whether or not any filter is set, unless {@link onlySuppressed} asks
   * for them specifically.
   */
  hideRated: boolean;

  /** Skip dryings this reviewer has merely stepped past. Off by default, per D10. */
  hideSeen: boolean;

  /**
   * Show **only** dryings this reviewer rated one of these values, for the current model version.
   *
   * The ticket's "my last ratings, e.g. only all dryings with rating 1-2": a way back to the
   * predictions already judged poor, rather than a fresh pass. Empty means no restriction.
   *
   * Contradicts {@link hideRated} by construction — one keeps only rated dryings, the other drops
   * them all — so the queue screen offers them as alternatives rather than as two checkboxes. When
   * both somehow arrive set, this one wins, being the more specific request.
   */
  onlyRatings: number[];
}

/**
 * Result of the queue-screen count (D20).
 *
 * Counts **dryings the pass will actually show**, with every filter applied — including the three
 * Firestore cannot express (rated / seen / suppressed), which are subtracted from the reviewer's own
 * stores. Counting devices instead, as this first did, cannot reflect a chain-level filter at all: a
 * device matches whether or not any of its dryings survive the age, active or rating filter, so the
 * figure stayed at "über 1000" no matter how narrow the pass really was.
 *
 * `firebase@7.24.0` has no `count()` aggregation, so a total means reading documents, and the two
 * flags say which direction an inexact figure errs in. They are mutually exclusive in the UI, and
 * `capped` wins when both hold: it understates by potentially thousands, while `approximate`
 * overstates by at most the truncated tail.
 */
export interface RenoReviewQueueCount {
  count: number;
  /**
   * The scan hit its cap, so `count` is a **lower** bound and reads "über {count}".
   *
   * Unavoidable for a pass with no narrowing filter: the queue holds tens of thousands of dryings
   * and reading them all on every filter change is not something a live filter card can do.
   */
  capped: boolean;
  /**
   * An exclusion store hit its cap, so `count` may be **slightly high** and reads "ca. {count}".
   *
   * Only reachable for a reviewer whose seen or review history exceeds
   * `REVIEW_HISTORY_CAP` documents, i.e. after a pass of that many dryings.
   */
  approximate: boolean;
}

/**
 * A filter selection with nothing restricted, for one model version.
 *
 * Exported so callers that only care about one field do not have to spell out the rest, and so adding a
 * filter does not break every literal that builds this object.
 */
export function emptyReviewFilters(modelVersion: string): RenoReviewFilters {
  return {
    modelVersion,
    deviceTypes: [],
    maxAgeMonths: null,
    fromDate: null,
    activeOnly: false,
    clientId: null,
    hideRated: false,
    hideSeen: false,
    onlyRatings: [],
    onlyChainSuspect: false,
    onlyWithComment: false,
    onlySuppressed: false,
  };
}

/**
 * Parses a `YYYY-MM-DD` filter date, as it travels in a route query parameter.
 *
 * Built from parts rather than handed to `new Date(string)`, which reads a bare date as **UTC
 * midnight**: east of Greenwich that silently includes part of the previous day, and west of it
 * excludes part of the chosen one. A reviewer picking a date means midnight where they are.
 *
 * @returns The local start of that day, or null when the value is absent or not a date.
 */
export function parseFilterDate(value: string | null | undefined): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec((value ?? "").trim());

  if (!match) {
    return null;
  }

  const parsed = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3])
  );

  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Formats a filter date back into `YYYY-MM-DD`, in local time for the same reason.
 *
 * `toISOString()` would convert to UTC first and can therefore hand back the neighbouring day.
 */
export function formatFilterDate(date: Date | null): string {
  if (!date) {
    return "";
  }

  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");

  return `${date.getFullYear()}-${month}-${day}`;
}

/** Builds the deterministic queue document id Assist writes. */
export function renoReviewQueueDocId(serialNo: string, chainId: string): string {
  return `${serialNo}__${chainId}`;
}

/** Top-level collection holding one document per reviewer. */
export const RENO_REVIEWERS_COLLECTION = "renoReviewers";

/** Per-reviewer quality verdicts. One per drying **per model version** (D2). */
export const RENO_REVIEWS_SUBCOLLECTION = "reviews";

/** Per-reviewer "never show this again" list. Version-independent and permanent (D5). */
export const RENO_SUPPRESSIONS_SUBCOLLECTION = "suppressions";

/**
 * Per-reviewer record of every drying stepped past, rated or not (D6).
 *
 * Per model version, like a review: a new RENO release should present the whole fleet again. A rated
 * drying is also a seen drying, so this is the superset.
 */
export const RENO_SEEN_SUBCOLLECTION = "seen";

/**
 * Per-reviewer free-text remark and data-quality report for one drying.
 *
 * Its own store because neither field is a verdict: the security rules require a review document to
 * carry a rating of 1-4, so a comment or a chain report written on a drying the reviewer could not
 * score would be refused. Version-independent, like a suppression.
 */
export const RENO_NOTES_SUBCOLLECTION = "notes";

/**
 * Cap on a reviewer's comment.
 *
 * Enforced in the field, in the repository and in the security rules, so a paste that outgrows it is
 * trimmed rather than refused by the backend after the reviewer has already moved on.
 */
export const RENO_COMMENT_MAX_LENGTH = 2000;

/**
 * Key identifying a reviewer.
 *
 * **Both ids, because a user id is only unique within its client.** Two admin-portal clients could
 * each hold a user with the same id, and keying on the user alone would silently merge their review
 * histories. The security rule reconstructs this same string from the token, so the format is part
 * of the contract, not a display detail.
 */
export function renoReviewerKey(clientId: string, userId: string): string {
  return `${clientId}__${userId}`;
}

/**
 * Document id of one reviewer's verdict on one drying.
 *
 * **The model version is part of the key** (D2). A new RENO release must re-open every drying for a
 * fresh pass rather than overwrite the judgement of the previous model, so R2.0 and R3.0 verdicts on
 * the same drying are different documents. Storing the version only as a field would have lost the
 * older rating the moment the drying was re-reviewed.
 */
export function renoReviewDocId(
  serialNo: string,
  chainId: string,
  modelVersion: string
): string {
  return `${serialNo}__${chainId}__${modelVersion}`;
}

/**
 * Document id of a suppression.
 *
 * Deliberately carries **no** model version (D5): "do not show this again" must survive every future
 * RENO release, and a versioned key would make it evaporate on the next one.
 */
export function renoSuppressionDocId(serialNo: string, chainId: string): string {
  return renoReviewQueueDocId(serialNo, chainId);
}

/**
 * Document id of a reviewer's note on one drying.
 *
 * Version-independent, like a suppression rather than like a review: a free-text remark about a
 * drying, and a report that its program chain looks broken, both outlive the RENO release they were
 * written under. A versioned key would drop a "this chain is corrupt" report on the next release,
 * when the chain is just as corrupt as before. The version is kept as a field for context.
 */
export function renoNoteDocId(serialNo: string, chainId: string): string {
  return renoReviewQueueDocId(serialNo, chainId);
}

/**
 * Document id of a "seen" record.
 *
 * Same shape as a review id, for the same reason: seen-ness is per model version.
 */
export function renoSeenDocId(
  serialNo: string,
  chainId: string,
  modelVersion: string
): string {
  return renoReviewDocId(serialNo, chainId, modelVersion);
}

/** Worst prediction quality on the reviewer's scale. */
export const RENO_RATING_MIN = 1;

/** Best prediction quality on the reviewer's scale. */
export const RENO_RATING_MAX = 4;

/** The selectable quality ratings, worst first. */
export const RENO_QUALITY_RATINGS = [1, 2, 3, 4];

/**
 * What the reviewer had on screen when they scored, captured with the score.
 *
 * The rating judges the *prediction* (D1), and predictions are recomputed: the backfill deletes the
 * daily documents of older model sets. Without this snapshot a stored "2" becomes unattributable as
 * soon as the chain is recalculated, which would defeat the purpose of collecting ratings at all.
 *
 * Taken from the queue entry, so it costs no extra read. The daily execution id is **not** captured:
 * the review screen deliberately does not load the predictions API, and adding a request per drying
 * to record it would slow every step of a pass measured in thousands of them.
 */
export interface RenoReviewContext {
  ratedTrafficLight: string | null;
  ratedQualityScore: number | null;
  /** The underlying artifact bundle behind `modelVersion`, for later analysis. */
  modelSet: string | null;
}

/**
 * One reviewer's verdict on one drying's RENO prediction.
 *
 * Lives at `renoReviewers/{userKey}/reviews/{serialNo}__{chainId}__{modelVersion}`. Per reviewer
 * because the ticket asks for independent ratings: two reviewers disagreeing is signal, not a
 * conflict to resolve.
 */
export interface RenoReview extends RenoReviewContext {
  userKey: string;
  /** The reviewer's own client. */
  clientId: string;
  /** The reviewer. */
  userId: string;
  ratedByNickname: string | null;

  serialNo: string;
  chainId: string;
  deviceType: RenoReviewDeviceType;
  /** The *drying's* owning client, distinct from the reviewer's. */
  clientIdOfDrying: string | null;
  modelVersion: string;

  /** {@link RENO_RATING_MIN}-{@link RENO_RATING_MAX}, bad to good. Never 0: see the suppressions store. */
  rating: number;
  ratedAt: firestore.Timestamp;
  updatedAt: firestore.Timestamp;
}

/**
 * One drying the reviewer has stepped past.
 *
 * Written whether or not they scored it. A seen-but-unrated drying **stays in the queue** by default
 * (D10): an accidental Weiter must not silently drop a drying from the pass, so hiding these is an
 * opt-in filter rather than the default behaviour.
 */
export interface RenoReviewSeen {
  userKey: string;
  serialNo: string;
  chainId: string;
  modelVersion: string;
  seenAt: firestore.Timestamp;
}

/**
 * One reviewer's note on one drying.
 *
 * Two independent things in one document, because they are written by the same screen at the same
 * moment and neither is a rating:
 *
 * - `comment` is free text. It may or may not be about the chain, so it is deliberately **not** the
 *   reason field of `chainSuspect`.
 * - `chainSuspect` reports that the program chain itself looks wrong, which is a complaint about the
 *   measured data rather than about the prediction the reviewer was asked to judge.
 *
 * An empty note (no comment, not suspect) is still written once the reviewer clears a previous one,
 * so clearing is recorded rather than ignored.
 */
export interface RenoReviewNote {
  userKey: string;
  clientId: string;
  userId: string;
  serialNo: string;
  chainId: string;
  /** Audit only: which model version was on screen when the note was last written. */
  modelVersion: string;
  /** Null when there is no comment, so a cleared one survives `filterOutUndefined`-style writes. */
  comment: string | null;
  chainSuspect: boolean;
  updatedAt: firestore.Timestamp;
}

/**
 * One drying the reviewer never wants to see again.
 *
 * Its own store rather than a rating of 0, because suppressing a drying says nothing about how good
 * the prediction was; folding it into the scale would corrupt any average taken over the scale, and
 * a versioned key would not survive the next RENO release.
 */
export interface RenoReviewSuppression {
  userKey: string;
  serialNo: string;
  chainId: string;
  suppressedAt: firestore.Timestamp;
  /** Audit only: which model version was on screen when the reviewer hid it. */
  suppressedUnderModelVersion: string;

  /**
   * Enough of the drying to name it on the un-hide screen, captured at hide time.
   *
   * Denormalised so that screen needs no second read per row. A `chainId` alone is not something a
   * person can recognise, and the alternative — looking each one up in the queue index — would put
   * one read per row on a screen whose whole job is showing a short list.
   */
  chainNumber: number | null;
  chainStart: firestore.Timestamp;
}

/** Whether a stored value is a rating this UI can render, rather than data from a future version. */
export function isValidRenoRating(rating: number): boolean {
  return (
    Number.isInteger(rating) &&
    rating >= RENO_RATING_MIN &&
    rating <= RENO_RATING_MAX
  );
}
